import React, { useEffect, useState, useCallback } from 'react';
import { FaPlus, FaCertificate, FaPaperclip, FaCheckCircle, FaClock, FaTimesCircle, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import { openPdfInline } from '../../shared/PdfViewer/PdfViewerHost';
import './MyQualifications.css';

const MyQualifications = () => {
  const [me, setMe] = useState(null);
  const [taxonomy, setTaxonomy] = useState([]);
  const [held, setHeld] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    qualification_id: '', awarded_at: '', expires_at: '', file: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meR = await fetch(getApiEndpoint('/api/users/me'), { headers: getAuthHeader() });
      const meD = meR.ok ? await meR.json() : null;
      if (!meD?.id) throw new Error('Not signed in');
      setMe(meD);
      const [tR, hR] = await Promise.all([
        fetch(getApiEndpoint('/api/qualifications'), { headers: getAuthHeader() }).then(r => r.ok ? r.json() : { qualifications: [] }),
        fetch(getApiEndpoint(`/api/users/${meD.id}/qualifications`), { headers: getAuthHeader() }).then(r => r.ok ? r.json() : { qualifications: [] }),
      ]);
      setTaxonomy(Array.isArray(tR.qualifications) ? tR.qualifications : []);
      setHeld(Array.isArray(hR.qualifications) ? hR.qualifications : []);
    } catch (_) { toast.error('Could not load your qualifications'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Mark qualifications the engineer already holds (pending or approved) but
  // still show every option in the dropdown so the list matches what their
  // contractor admin and FM see.
  const heldQualIds = new Set(held.filter(h => h.status !== 'rejected').map(h => h.qualification_id));

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.qualification_id) return toast.error('Pick a qualification');
    if (!form.file) return toast.error('Upload your certificate as evidence');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('qualification_id', String(form.qualification_id));
      if (form.awarded_at) fd.append('awarded_at', form.awarded_at);
      if (form.expires_at) fd.append('expires_at', form.expires_at);
      fd.append('evidence', form.file);
      const r = await fetch(getApiEndpoint(`/api/users/${me.id}/qualification-requests`), {
        method: 'POST', headers: getAuthHeader(), body: fd,
      });
      if (!r.ok && r.status !== 202) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed');
      }
      toast.success('Request submitted — your contractor admin will review and approve');
      setShowRequest(false);
      setForm({ qualification_id: '', awarded_at: '', expires_at: '', file: null });
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const viewEvidence = async (uq) => {
    try {
      const r = await fetch(
        getApiEndpoint(`/api/users/${me.id}/qualifications/${uq.id}/evidence`),
        { headers: getAuthHeader() }
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const isPdf = (uq.evidence_original_filename || '').toLowerCase().endsWith('.pdf') || blob.type === 'application/pdf';
      if (isPdf) {
        openPdfInline(url, uq.evidence_original_filename || 'Certificate');
        setTimeout(() => window.URL.revokeObjectURL(url), 5 * 60 * 1000);
      } else {
        window.open(url, '_blank');
      }
    } catch (_) { toast.error('Could not open evidence'); }
  };

  const cancelRequest = async (uq) => {
    if (!window.confirm('Cancel this pending request?')) return;
    try {
      const r = await fetch(
        getApiEndpoint(`/api/users/${me.id}/qualifications/${uq.id}`),
        { method: 'DELETE', headers: getAuthHeader() }
      );
      if (!r.ok) throw new Error();
      toast.success('Request cancelled');
      load();
    } catch (_) { toast.error('Failed to cancel'); }
  };

  const isExpired = (uq) => uq.expires_at && new Date(uq.expires_at) < new Date();

  const grouped = {
    approved: held.filter(h => h.status === 'approved'),
    pending: held.filter(h => h.status === 'pending'),
    rejected: held.filter(h => h.status === 'rejected'),
  };

  return (
    <div className="cs-mq-page">
      <div className="cs-mq-header">
        <div>
          <h1 className="cs-mq-title">
            <FaCertificate className="cs-mq-title-icon" />
            My Qualifications
          </h1>
          <p className="cs-mq-sub">
            Submit your certificate to request a qualification. Your contractor admin reviews and approves — once approved, you can be assigned to jobs that require it.
          </p>
        </div>
        <button type="button" className="cs-mq-add" onClick={() => setShowRequest(true)} disabled={taxonomy.length === 0 || heldQualIds.size >= taxonomy.length}>
          <FaPlus /> Request a qualification
        </button>
      </div>

      {loading ? (
        <div className="cs-mq-state">Loading…</div>
      ) : (
        <div className="cs-mq-groups">
          {grouped.pending.length > 0 && (
            <Section title="Awaiting approval" tone="pending" icon={<FaClock />}>
              {grouped.pending.map(uq => (
                <Row
                  key={uq.id}
                  uq={uq}
                  isExpired={isExpired(uq)}
                  onView={() => viewEvidence(uq)}
                  onCancel={() => cancelRequest(uq)}
                />
              ))}
            </Section>
          )}

          {grouped.approved.length > 0 && (
            <Section title="Approved" tone="approved" icon={<FaCheckCircle />}>
              {grouped.approved.map(uq => (
                <Row
                  key={uq.id}
                  uq={uq}
                  isExpired={isExpired(uq)}
                  onView={uq.evidence_file_path ? () => viewEvidence(uq) : null}
                />
              ))}
            </Section>
          )}

          {grouped.rejected.length > 0 && (
            <Section title="Rejected" tone="rejected" icon={<FaTimesCircle />}>
              {grouped.rejected.map(uq => (
                <Row
                  key={uq.id}
                  uq={uq}
                  isExpired={false}
                  onView={uq.evidence_file_path ? () => viewEvidence(uq) : null}
                  showRejectionReason
                />
              ))}
            </Section>
          )}

          {held.length === 0 && (
            <div className="cs-mq-empty">
              <FaCertificate className="cs-mq-empty-icon" />
              <h3>No qualifications yet</h3>
              <p>Click "Request a qualification" to submit your first certificate for approval.</p>
            </div>
          )}
        </div>
      )}

      {showRequest && (
        <div className="cs-mq-overlay" onClick={() => !submitting && setShowRequest(false)}>
          <form className="cs-mq-modal" onSubmit={submitRequest} onClick={e => e.stopPropagation()}>
            <h3>Request a qualification</h3>
            <p className="cs-mq-modal-sub">Your contractor admin will review the certificate and approve.</p>
            <label>
              Qualification
              <select
                value={form.qualification_id}
                onChange={e => setForm({ ...form, qualification_id: e.target.value })}
                required
              >
                <option value="">Pick one…</option>
                {taxonomy.map(q => {
                  const alreadyHeld = heldQualIds.has(q.id);
                  return (
                    <option key={q.id} value={q.id} disabled={alreadyHeld}>
                      {q.name}{q.category ? ` (${q.category})` : ''}{alreadyHeld ? ' — already on file' : ''}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="cs-mq-form-row">
              <label>
                Awarded (optional)
                <input type="date" value={form.awarded_at} onChange={e => setForm({ ...form, awarded_at: e.target.value })} />
              </label>
              <label>
                Expires (optional)
                <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
              </label>
            </div>
            <label>
              Certificate <span className="cs-mq-required">*</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setForm({ ...form, file: e.target.files[0] || null })}
                required
              />
              <small>PDF or image, up to 10MB</small>
            </label>
            <div className="cs-mq-form-actions">
              <button type="button" onClick={() => setShowRequest(false)} disabled={submitting}>Cancel</button>
              <button type="submit" className="primary" disabled={submitting}>
                <FaPaperclip /> {submitting ? 'Submitting…' : 'Submit for approval'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, tone, icon, children }) => (
  <section className={`cs-mq-section tone-${tone}`}>
    <h2 className="cs-mq-section-title">{icon} {title}</h2>
    <ul className="cs-mq-list">{children}</ul>
  </section>
);

const Row = ({ uq, isExpired, onView, onCancel, showRejectionReason }) => (
  <li className={`cs-mq-row ${isExpired ? 'expired' : ''}`}>
    <div className="cs-mq-row-main">
      <strong>{uq.qualification_name}</strong>
      {uq.qualification_category && <span className="cs-mq-cat"> · {uq.qualification_category}</span>}
      <div className="cs-mq-dates">
        {uq.awarded_at && <>Awarded {new Date(uq.awarded_at).toLocaleDateString('en-GB')}</>}
        {uq.awarded_at && uq.expires_at && ' · '}
        {uq.expires_at && (
          <span className={isExpired ? 'cs-mq-expired-text' : ''}>
            Expires {new Date(uq.expires_at).toLocaleDateString('en-GB')}
          </span>
        )}
      </div>
      {showRejectionReason && uq.rejection_reason && (
        <div className="cs-mq-reject-reason">Reason: {uq.rejection_reason}</div>
      )}
    </div>
    <div className="cs-mq-row-actions">
      {onView && (
        <button type="button" onClick={onView} title="View certificate">
          <FaPaperclip /> Certificate
        </button>
      )}
      {onCancel && (
        <button type="button" className="cs-mq-row-cancel" onClick={onCancel} title="Cancel request">
          <FaTrash /> Cancel
        </button>
      )}
    </div>
  </li>
);

export default MyQualifications;
